import { Suspense, useEffect, useState } from "react";
import { useSearchParams } from "@/lib/next-compat";
import { SyncBar } from "@/components/SyncBar";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { ToneCell, BulkToneBar } from "@/components/ToneTools";
import { cleanJournalist } from "@/lib/clean";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "./detail";
import { Highlight } from "@/components/Highlight";
import { Link } from "react-router-dom";

type Row = { id: string; publishedAt: string; title: string; url: string | null; sourceType: string; attachments?: any[]; sentimentLabel: string | null; journalist: string | null; mediaName?: string | null; mediaTier?: number | null; spokespersons?: string[]; adValueBw: number | null; newsValue: number | null; locationName?: string | null; categories: string[] };
const MT_LABEL: Record<string, string> = { news_online: "Online", print: "Cetak", tv: "TV", radio: "Radio" };

const SENT = [["", "Semua tone"], ["positive", "Positif"], ["neutral", "Netral"], ["negative", "Negatif"]] as const;
// Media tier Digivla: 1 Nasional, 2 Regional, 3 Lokal. Null = tak berjenjang.
const TIER_LABEL: Record<string, string> = { "1": "Tier 1 · Nasional", "2": "Tier 2 · Regional", "3": "Tier 3 · Lokal" };
const TIER_SHORT: Record<string, string> = { "1": "T1", "2": "T2", "3": "T3" };
const TIER_FILTERS = [["", "Semua tier"], ["1", "Tier 1"], ["2", "Tier 2"], ["3", "Tier 3"], ["none", "Tanpa tier"]] as const;
const MT_FILTERS = [["news_online,print,tv,radio", "Semua"], ["news_online", "Online"], ["print", "Cetak"], ["tv", "TV"], ["radio", "Radio"]] as const;
const SIZES = [25, 50, 100, 200];
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };

export default function ArticlesPage() {
  return <Suspense><ArticlesInner /></Suspense>;
}
function ArticlesInner() {
  const sp = useSearchParams();
  const locFilter = sp.get("location");
  const sentFilter = sp.get("sentiment");
  const catFilter = sp.get("category");
  const srcFilter = sp.get("source");
  const srcLabel = sp.get("sourceLabel");
  const personFilter = sp.get("person");
  const provFilter = sp.get("province");
  const provLabel = sp.get("provinceLabel");

  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState(sp.get("q") ?? "");
  const [qField, setQField] = useState(sp.get("qField") ?? "title");
  const [cat, setCat] = useState<string>(sp.get("category") ?? "");
  const [cats, setCats] = useState<string[]>([]);
  const [label, setLabel] = useState<string>(sp.get("label") ?? ""); // VendorLabel.id — filter by label AI
  const [labels, setLabels] = useState<Array<{ id: string; name: string; type: string; count: number }>>([]);
  // Dibaca dari URL supaya kartu ringkasan di Analytics bisa menautkan langsung
  // ke daftar yang sudah tersaring. Sebelumnya parameter ini diabaikan diam-diam
  // dan tautan mendarat di daftar "Semua" — terlihat seperti angka tidak cocok.
  const [mt, setMt] = useState<string>(sp.get("sourceType") ?? MT_FILTERS[0][0]);
  const [sent, setSent] = useState<string>(sp.get("sentiment") ?? "");
  const [tier, setTier] = useState<string>(sp.get("tier") ?? "");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [meta, setMeta] = useState<{ total: number; totalPages: number }>({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sortKey, setSortKey] = useState(sp.get("sort") ?? "");
  const [sortDir, setSortDir] = useState(sp.get("dir") ?? "desc");
  const [editTone, setEditTone] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const pageIds = rows.map((r: any) => r.id);
  const allSel = pageIds.length > 0 && pageIds.every((i2: string) => sel.has(i2));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(pageIds));
  const toggleSel = (id: string) => setSel((p2) => { const n = new Set(p2); n.has(id) ? n.delete(id) : n.add(id); return n; });
  function clickSort(k: string) {
    const nd = sortKey === k ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(k); setSortDir(nd); setPage(1);
    load({ page: 1, sortKey: k, sortDir: nd });
  }
  const [detailId, setDetailId] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(sp.get("start") ?? defStart);
  const [end, setEnd] = useState(sp.get("end") ?? today);

  async function load(opts?: { page?: number; pageSize?: number; mt?: string; start?: string; end?: string; cat?: string; sent?: string; sortKey?: string; sortDir?: string; label?: string; tier?: string }) {
    setLoading(true);
    const P = opts?.page ?? page, S = opts?.pageSize ?? pageSize;
    const p = new URLSearchParams({
      sourceType: opts?.mt ?? mt, start: opts?.start ?? start, end: opts?.end ?? end,
      page: String(P), pageSize: String(S),
    });
    p.set("sourceType", opts?.mt ?? mt);
    // Tidak filter vendor — media massa = semua vendor (digivla, google_news, dll.)
    // Tenant production punya digivla, tenant demo punya google_news; keduanya sourceType=news_online/print/tv/radio
    if (q) { p.set("q", q); p.set("qField", qField); }
    const SN = opts?.sent ?? sent; if (SN) p.set("sentiment", SN);
    const sk = opts?.sortKey ?? sortKey, sd = opts?.sortDir ?? sortDir;
    if (sk) { p.set("sort", sk); p.set("dir", sd); }
    if (locFilter) p.set("location", locFilter);
    if (sentFilter) p.set("sentiment", sentFilter);
    const catEff = (opts && "cat" in opts ? (opts as any).cat : cat) || catFilter;
    if (catEff) p.set("category", catEff);
    if (srcFilter) p.set("source", srcFilter);
    const lbl = opts && "label" in opts ? opts.label : label;
    if (lbl) p.set("label", lbl);
    const tr = opts && "tier" in opts ? opts.tier : tier;
    if (tr) p.set("tier", tr);
    if (personFilter) p.set("person", personFilter);
    if (provFilter) p.set("province", provFilter);
    const r = await fetch(`/api/v1/mentions?${p}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json().catch(() => null);
    if (!j) { setRows([]); setMeta({ total: 0, totalPages: 1 }); setLoading(false); setErr(`API error HTTP ${r.status} — cek terminal web`); return; }
    setErr("");
    setRows(j.data ?? []);
    setMeta({ total: j.meta?.total ?? 0, totalPages: j.meta?.totalPages ?? 1 });
    setLoading(false);
  }
  useEffect(() => {
    load({ page: 1 });
    fetch("/api/v1/subjects/categories").then((r) => (r.ok ? r.json() : { data: [] })).then((j) => setCats(j.data ?? []));
    fetch(`/api/v1/labels/list?wing=media&start=${start}&end=${end}`).then((r) => (r.ok ? r.json() : { data: [] })).then((j) => setLabels(j.data ?? [])).catch(() => {});
  }, []);
  const go = (P: number) => { const cl = Math.min(Math.max(1, P), meta.totalPages); setPage(cl); load({ page: cl }); };
  const fmtRp = (n: number | null) => (n == null ? "" : "Rp " + n.toLocaleString("id-ID"));
  const pdfOf = (r: Row) => r.attachments?.find?.((x: any) => x?.type === "pdf");
  const from = meta.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, meta.total);
  const pageNums = (() => {
    const t = meta.totalPages, out: number[] = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(t, page + 2); i++) out.push(i);
    return out;
  })();

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Article List</h1>
          <div className="avio-t-sm avio-muted">Media massa · {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); setPage(1); load({ page: 1, start: s2, end: e2 }); }} />
        </div>
      </div>
      <SyncBar wing="media" />

      {(locFilter || sentFilter || catFilter || srcFilter || personFilter || provFilter) && (
        <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
          {[["fa-solid fa-location-dot", locFilter], ["fa-solid fa-bullseye", catFilter], ["fa-solid fa-newspaper", srcLabel ?? srcFilter], ["fa-solid fa-face-meh", sentFilter], ["fa-solid fa-user", personFilter], ["fa-solid fa-map", provLabel ?? provFilter]]
            .filter(([, v]) => v).map(([ic, v]) => (
              <span key={String(v)} className="avio-fchip">
                <i className={String(ic)} /> {v}
                <Link to="/a/monitoring/articles" title="Hapus filter"><i className="fa-solid fa-xmark" /></Link>
              </span>
            ))}
        </div>
      )}

      {/* ---------- TOOLBAR ---------- */}
      <div>
        <div className="avio-toolbar">
          <div className="avio-toolbar__row">
            <label className="avio-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input className="avio-input" placeholder="Cari judul, media, atau isi artikel..." value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (setPage(1), load({ page: 1 }))} />
            </label>
            <select className="avio-select" value={qField} onChange={(e) => setQField(e.target.value)} title="Cari berdasarkan kolom">
              <option value="title">Judul</option>
              <option value="media">Nama media</option>
              <option value="content">Isi</option>
            </select>
            <select className="avio-select" value={cat} onChange={(e) => { const v = e.target.value; setCat(v); setPage(1); load({ page: 1, cat: v }); }} style={{ minWidth: 170 }}>
              <option value="">Semua keyword</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="avio-select" value={label} onChange={(e) => { const v = e.target.value; setLabel(v); setPage(1); load({ page: 1, label: v }); }} style={{ minWidth: 180, display: labels.length ? undefined : "none" }} title="Filter berdasarkan label AI">
              <option value="">Semua label</option>
              {Object.entries(labels.reduce((acc: Record<string, typeof labels>, l) => { (acc[l.type] ??= []).push(l); return acc; }, {})).map(([type, items]) => (
                <optgroup key={type} label={type}>
                  {items.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
                </optgroup>
              ))}
            </select>
            <button className="avio-btn" data-variant="primary" onClick={() => { setPage(1); load({ page: 1 }); }} disabled={loading}>
              <i className="fa-solid fa-filter" /> Terapkan
            </button>
            <a className="avio-btn" data-variant="secondary"
              href={`/api/v1/exports/medkon?start=${start}&end=${end}${sent ? `&sentiment=${sent}` : ""}`}>
              <i className="fa-solid fa-file-excel" /> Export
            </a>
          </div>
          <div className="avio-toolbar__row">
            <div className="avio-segment">
              {MT_FILTERS.map(([val, label]) => (
                <button key={val} className="avio-segment__item" aria-pressed={mt === val}
                  onClick={() => { setMt(val); setPage(1); load({ page: 1, mt: val }); }}>{label}</button>
              ))}
            </div>
            <div className="avio-segment" title="Tier media (Digivla): 1 Nasional · 2 Regional · 3 Lokal">
              {TIER_FILTERS.map(([val, label]) => (
                <button key={"tier" + val} className="avio-segment__item" aria-pressed={tier === val}
                  onClick={() => { setTier(val); setPage(1); load({ page: 1, tier: val }); }}>{label}</button>
              ))}
            </div>
            <div className="avio-segment">
              {SENT.map(([val, label]) => (
                <button key={"s" + val} className="avio-segment__item" aria-pressed={sent === val}
                  onClick={() => { setSent(val); setPage(1); load({ page: 1, sent: val }); }}>{label}</button>
              ))}
            </div>
            <button className="avio-btn" data-variant={editTone ? "primary" : "secondary"} data-size="sm"
              onClick={() => { setEditTone(!editTone); setSel(new Set()); }}>
              <i className={editTone ? "fa-solid fa-check" : "fa-solid fa-user-pen"} /> {editTone ? "Selesai koreksi" : "Koreksi sentimen"}
            </button>
            <span className="avio-count" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
              <select className="avio-select" data-size="sm" value={pageSize}
                onChange={(e) => { const S = Number(e.target.value); setPageSize(S); setPage(1); load({ page: 1, pageSize: S }); }}>
                {SIZES.map((s2) => <option key={s2} value={s2}>{s2} / hal</option>)}
              </select>
              <span><b>{from}–{to}</b> dari <b>{meta.total.toLocaleString("id-ID")}</b> artikel</span>
            </span>
          </div>
          {editTone && (
            <div className="avio-banner" data-tone="info" style={{ margin: 0 }}>
              <i className="fa-solid fa-user-pen avio-banner__icon" />
              <div>Mode koreksi aktif — ubah tone per baris, atau centang beberapa baris (☑ di header = pilih semua) lalu ubah massal. Koreksi analis kebal ditimpa sync berikutnya.</div>
            </div>
          )}
          {err && (
            <div className="avio-banner" data-tone="danger" style={{ margin: 0 }}>
              <i className="fa-solid fa-triangle-exclamation avio-banner__icon" /><div>{err}</div>
            </div>
          )}
          {editTone && sel.size > 0 && <BulkToneBar selected={[...sel]} onClear={() => setSel(new Set())} onDone={() => { setSel(new Set()); load(); }} />}
        </div>

        {/* ---------- TABEL ---------- */}
        <div className="avio-tablewrap">
          {loading && <div className="avio-progress" />}
          <DataGrid
            cols={[
              ...(editTone ? [{ id: "sel", header: <input type="checkbox" checked={allSel} onChange={toggleAll} onClick={(e: any) => e.stopPropagation()} title="Pilih semua di halaman ini" style={{ cursor: "pointer" }} />, width: "max", cell: (r: any) => <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }} /> }] : []),
              { id: "date", header: "Tanggal", width: "max", sortable: true, cell: (r: Row) => (
                  <span style={{ whiteSpace: "nowrap" }}>
                    {new Date(new Date(r.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(0, 10)}
                    <span className="avio-sub" style={{ marginLeft: 6 }}>{new Date(new Date(r.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(11, 16)}</span>
                  </span>) },
              { id: "media", header: "Nama media", width: "min:150", cell: (r: Row) => (
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.mediaName ?? ""}>
                    {r.mediaName || <span className="avio-muted">—</span>}
                  </span>) },
              { id: "title", header: "Judul", width: "min:320", sortable: true, cell: (r: Row) => (
                  <span style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", minWidth: 0 }}>
                    <span className="avio-ava" style={{ background: "var(--color-surface-sunken)", color: "var(--color-text-secondary)" }}>
                      <i className={r.sourceType === "print" ? "fa-regular fa-newspaper" : r.sourceType === "tv" ? "fa-solid fa-tv" : r.sourceType === "radio" ? "fa-solid fa-radio" : "fa-solid fa-globe"} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Highlight text={r.title} extra={[q]} />
                        {r.sourceType === "news_online" && r.url && <a href={r.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Buka sumber asli" style={{ marginLeft: 6 }}><i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 11 }} /></a>}
                        {r.sourceType === "print" && pdfOf(r) && <a href={`/api/v1/assets/media/${pdfOf(r)!.vendorPath}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Buka PDF halaman" style={{ marginLeft: 6 }}><i className="fa-regular fa-file-pdf" /></a>}
                      </span>
                      <span className="avio-sub" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.mediaName ?? "—"}{MT_LABEL[r.sourceType] ? ` · ${MT_LABEL[r.sourceType]}` : ""}{r.categories.length ? ` · ${r.categories.join(", ")}` : ""}
                      </span>
                    </span>
                  </span>) },
              { id: "tone", header: "Tone", width: "max", sortable: true, cell: (r: Row) => editTone
                  ? <ToneCell id={r.id} value={r.sentimentLabel} provenance={(r as any).sentimentProvenance} onSaved={() => load()} />
                  : r.sentimentLabel
                    ? <span className="avio-chip" data-sentiment={r.sentimentLabel}>
                        <span className="avio-chip__label">{TONE_ID[r.sentimentLabel] ?? r.sentimentLabel}</span>
                        {(r as any).sentimentProvenance === "human_override" && <i className="fa-solid fa-user-pen" title="Koreksi analis" style={{ marginLeft: 4, fontSize: 9 }} />}
                      </span>
                    : <span className="avio-muted">—</span> },
              { id: "tier", header: "Tier", width: "min:70", cell: (r: Row) => {
                  const t = r.mediaTier != null ? String(r.mediaTier) : null;
                  return t
                    ? <span className="avio-chip" data-tone={t === "1" ? "brand" : "soft"} title={TIER_LABEL[t] ?? `Tier ${t}`}><span className="avio-chip__label">{TIER_SHORT[t] ?? `T${t}`}</span></span>
                    : <span className="avio-muted">—</span>;
                } },
              { id: "journalist", header: "Jurnalis", width: "max", sortable: true, cell: (r: Row) => cleanJournalist(r.journalist) || <span className="avio-muted">—</span> },
              { id: "spox", header: "Narasumber", width: "min:150", cell: (r: Row) => {
                  const sx = r.spokespersons ?? [];
                  return sx.length
                    ? <span title={sx.join(", ")} style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {sx.slice(0, 2).map((n) => <span key={n} className="avio-chip" data-tone="soft"><span className="avio-chip__label">{n}</span></span>)}
                        {sx.length > 2 && <span className="avio-sub">+{sx.length - 2}</span>}
                      </span>
                    : <span className="avio-muted">—</span>;
                } },
              { id: "advalue", header: "Ad Value", width: "max", sortable: true, align: "right", cell: (r: Row) => fmtRp(r.adValueBw) || <span className="avio-muted">—</span> },
              { id: "newsvalue", header: "News Value", width: "max", sortable: true, align: "right", cell: (r: Row) => (
                  <span style={{ color: (r.newsValue ?? 0) < 0 ? "var(--sent-neg)" : undefined, fontWeight: "var(--weight-medium)" as any }}>{fmtRp(r.newsValue) || "—"}</span>) },
            ] as GridCol<Row>[]}
            data={rows}
            sortMode="server"
            serverSort={sortKey ? { key: sortKey, dir: sortDir as "asc" | "desc" } : null}
            onServerSort={(k) => clickSort(k === "time" ? "date" : k)}
            onRowClick={(r) => setDetailId(r.id)}
            minTableWidth={1080}
            emptyText="Tidak ada artikel untuk filter ini."
          />
        </div>
      </div>

      {/* ---------- PAGINASI ---------- */}
      <div className="avio-pagination" style={{ justifyContent: "center" }}>
        <button className="avio-page-btn" onClick={() => go(1)} disabled={page === 1 || loading} title="Halaman pertama">«</button>
        <button className="avio-page-btn" onClick={() => go(page - 1)} disabled={page === 1 || loading}>‹</button>
        {pageNums[0] > 1 && <span className="avio-sub">…</span>}
        {pageNums.map((n) => (
          <button key={n} className="avio-page-btn" aria-current={n === page ? "page" : undefined} onClick={() => go(n)} disabled={loading}>{n}</button>
        ))}
        {pageNums[pageNums.length - 1] < meta.totalPages && <span className="avio-sub">…</span>}
        <button className="avio-page-btn" onClick={() => go(page + 1)} disabled={page >= meta.totalPages || loading}>›</button>
        <button className="avio-page-btn" onClick={() => go(meta.totalPages)} disabled={page >= meta.totalPages || loading} title="Halaman terakhir">»</button>
        <span className="avio-sub" style={{ marginLeft: "var(--space-5)" }}>hal. {page} dari {meta.totalPages}</span>
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
